import {
    IMemberMenuDropdownStylesData, IMemberMenuIconStylesData, IMemberMenuStyleData, IMemberMenuWidgetData,
    MemberMenuIconArrangement, MemberMenuLayout, MemberMenuWidgetData, IInteractiveElementColorData, IMemberMenuShortData
} from "@pageBuilder/typings";
import { MemberMenuScriptRenderer } from "./member-menu-script-renderer";
import { CssProperties } from "../../../../models/css-property";
import { WidgetRendererBase } from "../../widget-renderer-base";
import { IRenderingCommonContext } from "@commonRendering/types";
import { Base64Util } from "src/utils/base64-util";
import { PbScriptsService } from "src/services/pb.scripts.service";

export class MemberMenuWidgetRenderer extends WidgetRendererBase<IMemberMenuWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-mobile-member-menu-links";
    private readonly scriptRenderer: MemberMenuScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new MemberMenuScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("memberMenuWidget");
        element.classList.add("alignable");

        return element;
    }

    protected renderInternal(element: HTMLElement, data: IMemberMenuWidgetData): HTMLElement {
        this.cleanupInternal(element);

        const authInfoWrapper = document.createElement("div");
        authInfoWrapper.classList.add("auth-info-wrapper", "with-text");
        authInfoWrapper.setAttribute("data-toggle", "dropdown");

        this.renderAuthInfoWrapper(authInfoWrapper, data);
        const content = element.querySelector(".widget-content") as HTMLElement;
        content?.append(authInfoWrapper);

        this.renderStyles(element, data);
        this.setAttributes(element, data);
        this.renderScript(element, data);

        PbScriptsService.scripts?.MemberMenuWidget?.handleChanges(element);
        return element;
    }

    private renderAuthInfoWrapper(element: HTMLElement, data: IMemberMenuWidgetData): void {
        const authTextSpan = document.createElement("span");
        authTextSpan.classList.add("auth-text");

        if (data.layout === MemberMenuLayout.Text) {
            element.append(authTextSpan);
        } else {
            if (data.layout === MemberMenuLayout.TextWithBackground) {
                element.classList.add("with-background");
            }

            this.renderIconAndTextLayout(element, data, authTextSpan);
        }
    }

    private renderIconAndTextLayout(element: HTMLElement, data: IMemberMenuWidgetData, authTextSpan: HTMLElement): void {
        const authAvatarImage = document.createElement("img");
        authAvatarImage.classList.add("auth-icon");

        if (data.iconArrangement === MemberMenuIconArrangement.AlignLeft) {
            element.append(authAvatarImage, authTextSpan);
        } else {
            element.append(authTextSpan, authAvatarImage);
        }
    }

    protected renderStyles(element: HTMLElement, data: IMemberMenuWidgetData): void {
        if (!data.style) {
            return;
        }

        this.renderBaseStyles(element, data.style);
        this.renderAuthInfoWrapperStyle(element, data);
        this.renderIconStyle(element, data.style.icon);
        this.renderDropdownStyle(element, data.style?.dropdown);
        this.renderAuthTextStyle(element, data.style.linkColorStyleData);
    }

    private renderBaseStyles(element: HTMLElement, style: IMemberMenuStyleData): void {
        this.setAlignment(element, style);
        this.setHeight(element, style);
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private setAlignment(element: HTMLElement, style: IMemberMenuStyleData): void {
        element.classList.add(style.alignment ? `justify-${style.alignment}` : `justify-${MemberMenuWidgetData.defaultStyle.alignment}`);
    }

    private setHeight(element: HTMLElement, style: IMemberMenuStyleData): void {
        const elementHeight = style.heightData?.height ? `${style.heightData.height}px` : "auto";
        element.style.height = elementHeight;
        element.style.minHeight = elementHeight;
    }

    private renderAuthInfoWrapperStyle(element: HTMLElement, data: IMemberMenuWidgetData): void {
        const authInfoWrapperProp = new CssProperties();

        if (data.style.backgroundColor) {
            authInfoWrapperProp.tryAdd("background-color", data.style.backgroundColor);
        }

        if (data.style.borderRadius) {
            authInfoWrapperProp.tryAdd("border-top-left-radius", `${data.style.borderRadius.topLeftPx}px`);
            authInfoWrapperProp.tryAdd("border-top-right-radius", `${data.style.borderRadius.topRightPx}px`);
            authInfoWrapperProp.tryAdd("border-bottom-left-radius", `${data.style.borderRadius.bottomLeftPx}px`);
            authInfoWrapperProp.tryAdd("border-bottom-right-radius", `${data.style.borderRadius.bottomRightPx}px`);
        }

        if (data.layout === MemberMenuLayout.TextWithBackground) {
            if (data.style.buttonColor?.colorDefault) {
                authInfoWrapperProp.tryAdd("background-color", data.style.buttonColor.colorDefault);
            }

            if (data.style.buttonColor?.colorHovered) {
                this.styleService.setStyleObject(element, `#${element.id} .auth-info-wrapper:hover`, {
                    "background-color": data.style.buttonColor.colorHovered
                });
            }
        }

        if (data.layout === MemberMenuLayout.IconAndText) {
            authInfoWrapperProp.tryAdd("grid-template-columns", "repeat(2, auto)");
        }

        this.styleService.setStyle(element, `#${element.id} .auth-info-wrapper`, authInfoWrapperProp.data);
    }

    private renderIconStyle(element: HTMLElement, style: IMemberMenuIconStylesData): void {
        const iconProp = new CssProperties();

        iconProp.tryAdd("--stroke-color", style.color ?? MemberMenuWidgetData.defaultIconStyle.color);
        iconProp.tryAdd("--fill-color", style.backgroundColor ?? MemberMenuWidgetData.defaultIconStyle.backgroundColor);

        this.styleService.setStyle(element, `#${element.id} .auth-icon`, iconProp.data);
    }

    private renderDropdownStyle(element: HTMLElement, style?: IMemberMenuDropdownStylesData): void {
        const dropdownProp = new CssProperties();
        dropdownProp.tryAdd("background-color", style?.backgroundColor ?? MemberMenuWidgetData.defaultDropdownStyle.backgroundColor);

        if (style?.borderRadius) {
            dropdownProp.tryAdd("border-top-left-radius", `${style?.borderRadius.topLeftPx}px`);
            dropdownProp.tryAdd("border-top-right-radius", `${style?.borderRadius.topRightPx}px`);
            dropdownProp.tryAdd("border-bottom-left-radius", `${style?.borderRadius.bottomLeftPx}px`);
            dropdownProp.tryAdd("border-bottom-right-radius", `${style?.borderRadius.bottomRightPx}px`);
        }

        this.styleService.setStyle(element, `#${element.id} .dropdown-menu`, dropdownProp.data);

        const dropdownItemProp = new CssProperties();
        dropdownItemProp.tryAdd("color", style?.linkColorStyleData?.colorDefault ?? MemberMenuWidgetData.defaultDropdownStyle.linkColorStyleData.colorDefault);

        this.styleService.setStyle(element, `#${element.id} .dropdown-menu-item`, dropdownItemProp.data);

        const dropdownItemHoverProp = new CssProperties();
        dropdownItemHoverProp.tryAdd("background-color", style?.itemHoverColor ?? MemberMenuWidgetData.defaultDropdownStyle.itemHoverColor);
        dropdownItemHoverProp.tryAdd("color", style?.linkColorStyleData?.colorHovered ?? MemberMenuWidgetData.defaultDropdownStyle.linkColorStyleData.colorHovered);

        this.styleService.setStyle(element, `#${element.id} .dropdown-menu-item:hover`, dropdownItemHoverProp.data);
    }

    private renderAuthTextStyle(element: HTMLElement, style: IInteractiveElementColorData): void {
        if (style.colorDefault) {
            this.styleService.setStyleObject(element, `#${element.id} .widget-content .auth-info-wrapper.with-text .auth-text:not(:hover)`, {
                "color": style.colorDefault
            });
        }

        if (style.colorHovered) {
            this.styleService.setStyleObject(element, `#${element.id} .widget-content .auth-info-wrapper.with-text .auth-text:hover`, {
                "color": style.colorHovered
            });
        }
    }

    private setAttributes(element: HTMLElement, data: IMemberMenuWidgetData): void {
        if (!this.renderingCommonContext.data.gdprInfo.isGdprEnabled) {
            element.setAttribute("data-gdpr-disabled", "true");
        }

        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }

        this.setMemberMenuLayoutAttribute(element, data.layout);

        const mobileHeaderSectionMenuData = this.getMemberMenuShortData(data);
        const json = Base64Util.serialize(mobileHeaderSectionMenuData) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
    }

    private getMemberMenuShortData(data: IMemberMenuWidgetData): IMemberMenuShortData {
        return {
            menuLinkItems: data.menuLinkItems
        };
    }

    private setMemberMenuLayoutAttribute(element: HTMLElement, leLayout: MemberMenuLayout): void {
        element.dataset.memberMenuLayout = `${leLayout}`;
    }

    private renderScript(element: HTMLElement, data: IMemberMenuWidgetData): void {
        const existingData = this.getData(element) as IMemberMenuWidgetData;

        this.scriptRenderer.renderCommon(this.renderedDocument);

        if (!MemberMenuWidgetData.localScriptRerenderNeeded(existingData, data)) {
            this.scriptRenderer.renderLocal(element);
        }
    }

    protected cleanupInternal(element: HTMLElement): void {
        this.cleanupStyle(element);
        this.clearAlignmentClasses(element);
        this.scriptRenderer.cleanupCommon(this.renderedDocument);
        const content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            return;
        }

        const authInfoWrapper = content.querySelector<HTMLElement>(".auth-info-wrapper");
        if (!authInfoWrapper) {
            return;
        }

        authInfoWrapper.remove();
    }

    private cleanupStyle(element: HTMLElement): void {
        const selectors = this.getContentSelectorsInternal(element);
        if (selectors && selectors.length > 0) {
            selectors.forEach(selector => this.styleService.clearStyle(element, selector));
        }
    }

    private clearAlignmentClasses(element: HTMLElement): void {
        element.classList.remove("justify-start");
        element.classList.remove("justify-center");
        element.classList.remove("justify-end");
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [
            `#${element.id}`,
            `#${element.id} .widget-content`,
            `#${element.id} .auth-info-wrapper`,
            `#${element.id} .auth-icon`,
            `#${element.id} .auth-info-wrapper .auth-text`,
            `#${element.id} .dropdown-menu`,
            `#${element.id} .dropdown-menu .divider`,
            `#${element.id} .dropdown-menu-item`,
            `#${element.id} .dropdown-menu-item:hover`,
            `#${element.id} .dropdown-menu-item .link`,
            `#${element.id} .widget-content .auth-info-wrapper.with-text .auth-text:not(:hover)`,
            `#${element.id} .widget-content .auth-info-wrapper.with-text .auth-text:hover`
        ];
    }

}
